import { LIFESTYLE } from "@/constants/testIds";
import { Briefcase, Dumbbell, Gift, Film, Sparkles } from "lucide-react";

const CARDS = [
  {
    title: "Office Snack",
    body: "Smart fuel for long afternoons. Quiet pouch, quiet desk.",
    icon: Briefcase,
    tone: "ink",
    image:
      "https://static.prod-images.emergentagent.com/jobs/096b6207-302a-4d30-852c-60e212c167c7/images/eb9bbdff2920db7857156daa3e9dd17c9269fdc21e1153df76745aaccafc9e1c.png",
  },
  {
    title: "Fitness Snack",
    body: "Protein, healthy fats and slow energy in every kernel.",
    icon: Dumbbell,
    tone: "cream",
  },
  {
    title: "Gift Box",
    body: "Pair the trio. The most thoughtful 'thinking of you'.",
    icon: Gift,
    tone: "terracotta",
  },
  {
    title: "Movie Night",
    body: "Trade the popcorn. Make it Honey Gold tonight.",
    icon: Film,
    tone: "gold",
    image:
      "https://static.prod-images.emergentagent.com/jobs/096b6207-302a-4d30-852c-60e212c167c7/images/8aaa11081405b82ebb73a9a6c7db68f93f5023b0c3c060be860b5fa0368bfa3d.png",
  },
  {
    title: "Premium Pantry",
    body: "The pouch that earns a place on your open shelf.",
    icon: Sparkles,
    tone: "ink",
  },
];

const toneClass = (tone) => {
  switch (tone) {
    case "ink":
      return "bg-[#1a3626] text-[#faf7f2]";
    case "cream":
      return "bg-[#fdfaf4] text-[#1a3626] border border-[#c5a059]/25";
    case "terracotta":
      return "bg-[#c86b5e] text-[#faf7f2]";
    case "gold":
      return "bg-[#c5a059] text-[#1a3626]";
    default:
      return "bg-white text-[#1a3626]";
  }
};

const iconBg = (tone) =>
  tone === "ink" || tone === "terracotta"
    ? "bg-[#faf7f2]/15 text-[#c5a059]"
    : tone === "gold"
      ? "bg-[#1a3626] text-[#c5a059]"
      : "bg-[#1a3626] text-[#c5a059]";

export const Lifestyle = () => {
  return (
    <section
      id="lifestyle"
      data-testid={LIFESTYLE.wrapper}
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="max-w-2xl mb-14">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#c5a059]" />
            <span className="overline">For Every Moment</span>
          </div>
          <h2 className="font-serif-display text-[40px] sm:text-[56px] leading-[1.05] text-[#1a3626] tracking-tight">
            A snack with{" "}
            <em className="italic text-[#8b5a2b]">a story.</em>
          </h2>
        </div>

        {/* Bento grid — 6 col responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-5 lg:gap-6">
          {CARDS.map((c, i) => {
            const Icon = c.icon;
            // Span allocations across 6-col grid
            const span =
              [
                "lg:col-span-3 lg:row-span-2", // Office (large with image)
                "lg:col-span-3",
                "lg:col-span-2",
                "lg:col-span-2 lg:row-span-2", // Movie tall with image
                "lg:col-span-2",
              ][i] || "lg:col-span-2";

            const hasImage = !!c.image;

            return (
              <article
                key={i}
                data-testid={LIFESTYLE.card(i)}
                className={`${span} ${toneClass(
                  c.tone
                )} rounded-[2rem] p-7 lg:p-8 relative overflow-hidden min-h-[220px] flex flex-col justify-end group transition-all duration-500 hover:-translate-y-1 shadow-[0_10px_40px_rgba(26,54,38,0.08)]`}
              >
                {hasImage && (
                  <>
                    <img
                      src={c.image}
                      alt={c.title}
                      className="absolute inset-0 w-full h-full object-cover opacity-50 transition-transform duration-[1200ms] group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a3626] via-[#1a3626]/70 to-[#1a3626]/10" />
                  </>
                )}
                <div
                  className={`relative w-11 h-11 rounded-full ${iconBg(
                    c.tone
                  )} flex items-center justify-center mb-5 self-start`}
                >
                  <Icon size={18} />
                </div>
                <h3 className="relative font-serif-display text-[26px] sm:text-[30px] leading-tight">
                  {c.title}
                </h3>
                <p
                  className={`relative mt-2 text-[14px] leading-[1.7] ${
                    c.tone === "ink" || c.tone === "terracotta" || hasImage
                      ? "text-[#faf7f2]/85"
                      : c.tone === "gold"
                        ? "text-[#1a3626]/85"
                        : "text-[#5c4033]"
                  }`}
                >
                  {c.body}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
